import { Component, effect, inject, signal } from '@angular/core';
import { I18nService } from '../../../../Shared/i18n/i18n.service';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthModalService } from '../../data/auth-modal.service';
import { CustomerAuthService } from '../../data/customer-auth.service';
import { CoinRules, CoinsService } from '../../data/coins.service';
import { AuthPanelComponent } from '../auth-panel/auth-panel';
import { BrandLogoDirective } from '../../../../Shared/brand-logo/brand-logo.directive';

/**
 * Storefront sign-in modal — opens from the header "Sign in" (or anywhere via AuthModalService).
 * Two-pane foodpanda-style layout (brand promo + auth panel); the promo pane collapses on mobile.
 * On success it loads the profile, closes, and navigates to the caller's returnUrl if one was set.
 */
@Component({
  selector: 'app-auth-modal',
  standalone: true,
  imports: [CommonModule, AuthPanelComponent, BrandLogoDirective],
  templateUrl: './auth-modal.html',
  styleUrls: ['./auth-modal.css'],
})
export class AuthModalComponent {
  protected readonly i18n = inject(I18nService);
  protected readonly authModal = inject(AuthModalService);
  private readonly auth = inject(CustomerAuthService);
  private readonly coins = inject(CoinsService);
  private readonly router = inject(Router);

  /**
   * What signing up actually earns, straight from the platform's ERP settings — the promo pane
   * advertises these rather than hard-coded copy, so editing /platform/coin-rewards changes the
   * pitch. Null until loaded (or if the call fails), in which case the pane falls back to its
   * generic line rather than showing a half-empty offer.
   */
  protected readonly rules = signal<CoinRules | null>(null);
  private requested = false;

  constructor() {
    // Fetched on first open, not at app start: this modal is mounted on every storefront page, and a
    // visitor who never signs in shouldn't pay for a request they'll never see the result of.
    effect(() => {
      if (!this.authModal.isOpen() || this.requested) return;
      this.requested = true;
      this.coins.rules().subscribe({
        next: (r) => this.rules.set(r.data ?? null),
        error: () => this.rules.set(null),
      });
    });
  }

  onAuthenticated(): void {
    const returnUrl = this.authModal.returnUrl();
    this.auth.loadProfile().subscribe({ error: () => {} });
    this.authModal.close();
    if (returnUrl) void this.router.navigateByUrl(returnUrl);
  }

  close(): void {
    this.authModal.close();
  }
}
